import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import type { ApolloCache } from '@apollo/client'
import SearchInput from '../../../../SearchInput/index'
import CommunityTabBar from '../CommunityTabBar/index'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import {
  StaticTextProvider,
  useTranslatedText,
  InternationalStrings,
} from '../../../../StaticText/StaticTextContext'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import { H5 } from '../../../../Heading/index'
import LoadMore from '../../../../LoadMore/LoadMore'
import { usePostHogEvent } from '../../../../PostHog/index'
import { isBrowser } from '../../../../../config/environment'
import {
  APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
} from '../../../../../services/apollo-graphql/index'
import {
  GET_FOLLOWERS,
  GET_FOLLOWING,
  CREATE_FOLLOW,
  DELETE_FOLLOW,
} from '../../../../../services/community/query'
import { createRemoveItemUpdater } from '../../../../../services/community/shared/cache-updaters'
import useDebouncedValue from '../../../../../services/community/shared/useDebouncedValue'
import type {
  UserProfileSummary,
  FollowConnection,
  FollowEdge,
  FollowResponse,
  UnfollowResponse,
} from '../../../../../services/community/types'
import ConnectionCard from '../ConnectionCard/ConnectionCard'

// Cursor page size for follower/following queries.  Search filtering is
// applied server-side via the `searchQuery` arg on `userProfileFollowers` /
// `userProfileFollowing`, so each page already reflects the active filter.
const PAGE_SIZE = 20

type TabKey = 'followers' | 'following'

// The `profile` payload shared by CREATE_FOLLOW / DELETE_FOLLOW responses.
// Derived from the exported response type so we don't need to export the
// module-private FollowMutationProfile from services/community/types.
type FollowProfile = FollowResponse['userProfileFollow']['profile']

export interface ConnectionsModalProps {
  uid: string
  initialTab?: TabKey
  onClose: () => void
  /** JWT token for authenticated queries — passed from parent to avoid Redux dependency */
  jwt?: string
  /**
   * Display name (or username) of the profile whose connections are being
   * shown. When provided, the modal title becomes "{name}'s Connections" so
   * users see whose follower / following list they're inspecting. When
   * omitted or empty, the title falls back to the static "Connections".
   * Pass the same string the surrounding profile UI shows (display name with
   * a username fallback).
   */
  displayName?: string
  /**
   * True when the viewer is looking at their own profile's connections.
   * Gates surgical updates to the viewer's `userProfileFollowing` cache slot
   * — totalCount bump on follow, edge removal on unfollow — neither of which
   * is correct on someone else's modal.
   */
  isViewerProfile?: boolean
  /**
   * UUID of the logged-in viewer. Used as the cache key for the surgical
   * `UserProfile:<viewerUuid>.followingCount` write that keeps the profile
   * gradient header in sync with follow/unfollow toggles on the viewer's
   * own modal.
   */
  viewerUuid?: string
  staticText: InternationalStrings
}

interface FollowersData {
  userProfileFollowers?: FollowConnection
}

interface FollowingData {
  userProfileFollowing?: FollowConnection
}

const LOADING_PLACEHOLDER_COUNT = 6

interface ConnectionsModalContentProps {
  uid: string
  initialTab: TabKey
  onClose: () => void
  jwt?: string
  displayName?: string
  isViewerProfile: boolean
  viewerUuid?: string
}

function ConnectionsModalContent ({
  uid,
  initialTab,
  onClose,
  jwt,
  displayName,
  isViewerProfile,
  viewerUuid,
}: ConnectionsModalContentProps) {
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab)
  const [search, setSearch] = useState('')

  // Debounced copy threaded into the GraphQL queries (page 1 useQuery + the
  // page-2+ apolloClient.query inside loadMoreFollowers / loadMoreFollowing).
  // The raw `search` stays bound to the <SearchInput> so the input itself
  // remains responsive.
  const debouncedSearch = useDebouncedValue(search, 250)

  const titleText = useTranslatedText({ keys: ['title'] })
  const titleWithNameTemplate = useTranslatedText({ keys: ['titleWithName'] })
  const followersLabel = useTranslatedText({ keys: ['followersLabel'] })
  const followingLabel = useTranslatedText({ keys: ['followingLabel'] })
  const searchPlaceholder = useTranslatedText({ keys: ['searchPlaceholder'] })
  const cardAnonymous = useTranslatedText({ keys: ['cardAnonymous'] })
  const cardFollowsYou = useTranslatedText({ keys: ['cardFollowsYou'] })
  const closeLabel = useTranslatedText({ keys: ['closeLabel'] })
  const emptySearchTemplate = useTranslatedText({ keys: ['emptySearch'] })
  const defaultDisplayName = useTranslatedText({ keys: ['defaultDisplayName'] })

  // Lock background page scroll while the modal is mounted so the page
  // scrollbar doesn't appear next to / behind the full-screen overlay.
  // Mirrors the CommunityProfileWelcome modal pattern.
  useEffect(() => {
    if (!isBrowser()) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  // Page 1 — Apollo cache. `network-only` for freshness/privacy on every
  // open; observer broadcasts when the mutation response normalizes the
  // target UserProfile entity, so pill state on page-1 rows auto-updates.
  const followersResult = useQuery<FollowersData>(GET_FOLLOWERS, {
    variables: { uid, first: PAGE_SIZE, searchQuery: debouncedSearch || undefined },
    context: { jwt },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  })

  const followingResult = useQuery<FollowingData>(GET_FOLLOWING, {
    variables: { uid, first: PAGE_SIZE, searchQuery: debouncedSearch || undefined },
    context: { jwt },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_NETWORK_ONLY,
  })

  // Pages 2+ — direct `apolloClient.query({ fetchPolicy: 'no-cache' })`,
  // accumulated in component state. Mirrors `useFollowedPlaylists`: the
  // useApolloListPagination + fetchMore + updateQuery + relayStylePagination
  // pairing in this repo loses merged state when Apollo broadcasts after
  // any mutation. `no-cache` bypasses the cache pipeline entirely, so the
  // accumulated edges can't be clobbered.
  //
  // ──────────────────────────────────────────────────────────────────
  // TWIN-CODE — keep in sync (see follow-up ticket for shared extraction):
  //   • `services/community/identity/library/useFollowedPlaylists.ts`
  //     (cursor variant — single instance)
  //   • `Community/Identity/Profile/ProfilePlaylistsSection/`
  //     `ProfilePlaylistsSection.tsx` (offset variant)
  // All three share: page-1 useQuery + pages-2+ no-cache query + local
  // extras accumulator + reset-on-key-change effect + reqGenRef.  When
  // editing any pagination logic here, audit those siblings too.
  // ──────────────────────────────────────────────────────────────────
  //
  // State resets on unmount by design — closing the modal and reopening
  // it (or switching to a different profile's modal) lands the user on
  // page 1 again.  This is the cost of the no-cache strategy and is
  // intentional UX for a modal.
  const [extraFollowers, setExtraFollowers] = useState<FollowEdge[]>([])
  const [followersCursor, setFollowersCursor] = useState<string | null>(null)
  const [followersHasMore, setFollowersHasMore] = useState<boolean>(false)
  const [isLoadingMoreFollowers, setIsLoadingMoreFollowers] = useState<boolean>(false)

  const [extraFollowing, setExtraFollowing] = useState<FollowEdge[]>([])
  const [followingCursor, setFollowingCursor] = useState<string | null>(null)
  const [followingHasMore, setFollowingHasMore] = useState<boolean>(false)
  const [isLoadingMoreFollowing, setIsLoadingMoreFollowing] = useState<boolean>(false)

  // Request-generation counters (per-tab).  Bumped synchronously inside
  // the reset effect; each loader captures the current value and bails on
  // resolve if it no longer matches.  Prevents the search-vs-pagination
  // race where a `loadMore*` started under filter A resolves *after* the
  // reset for filter B runs and appends stale unfiltered rows onto the
  // post-reset extras.  Local React state isn't cancellable on its own.
  const followersReqGenRef = useRef<number>(0)
  const followingReqGenRef = useRef<number>(0)
  // Synchronous in-flight latches — LoadMore's IntersectionObserver can fire
  // twice before React commits `setIsLoadingMore*`.
  const loadMoreFollowersInFlightRef = useRef<boolean>(false)
  const loadMoreFollowingInFlightRef = useRef<boolean>(false)

  // Reset per-tab pagination state when the modal switches to a different
  // profile (account change, navigating to another user's modal) OR when the
  // debounced search term flips — pages 2+ aren't in the Apollo cache and
  // mixing a filtered page 1 with stale unfiltered extras would be incorrect.
  useEffect(() => {
    followersReqGenRef.current += 1
    followingReqGenRef.current += 1
    setExtraFollowers([])
    setFollowersCursor(null)
    setFollowersHasMore(false)
    setExtraFollowing([])
    setFollowingCursor(null)
    setFollowingHasMore(false)
  }, [uid, debouncedSearch])

  // Seed cursor + hasMore from each tab's page-1 response once it lands.
  // Guarded on `extras.length === 0` so a later page-1 broadcast can't
  // clobber a cursor we've already advanced past.
  useEffect(() => {
    const page1 = followersResult.data?.userProfileFollowers
    if (!page1 || extraFollowers.length > 0) return
    setFollowersCursor(page1.pageInfo?.endCursor ?? null)
    setFollowersHasMore(!!page1.pageInfo?.hasNextPage)
  }, [followersResult.data, extraFollowers.length])

  useEffect(() => {
    const page1 = followingResult.data?.userProfileFollowing
    if (!page1 || extraFollowing.length > 0) return
    setFollowingCursor(page1.pageInfo?.endCursor ?? null)
    setFollowingHasMore(!!page1.pageInfo?.hasNextPage)
  }, [followingResult.data, extraFollowing.length])

  // Avatars are now included directly on UserProfileSummary via the fragment.
  // No separate batch query needed.
  const batchAvatarByUid: Record<string, { url?: string; small?: string; card?: string }> = {}

  const [followUser] = useMutation<FollowResponse>(CREATE_FOLLOW, {
    context: { jwt },
  })
  const [unfollowUser] = useMutation<UnfollowResponse>(DELETE_FOLLOW, {
    context: { jwt },
  })

  const apolloClient = useApolloClient()

  // Mount guard for async state updates — useFollowedPlaylists pattern.
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])

  // Modal-scoped follow/unfollow tracking — distinct from the
  // profile-gradient-header `community_member_profile_*` events because the
  // user intent differs (browsing a connections list vs. looking at a single
  // profile). Two surface-specific events; analysts can OR them in PostHog
  // to get a unified "any member follow" rollup.
  const trackMemberConnectionModalFollowed = usePostHogEvent('community_member_connection_modal_followed')
  const trackMemberConnectionModalUnfollowed = usePostHogEvent('community_member_connection_modal_unfollowed')

  // Page-2+ loaders, one per tab.  Splitting these (rather than branching
  // inside one `loadMoreActive` on `activeTab`) lets each callback's
  // dep list narrow to its own tab's state, so a re-render driven by
  // (say) followers state churn doesn't recreate the following loader.
  // `apolloClient` is a stable singleton — listed for exhaustive-deps but
  // never identity-flips.
  const loadMoreFollowers = useCallback(async (): Promise<void> => {
    if (loadMoreFollowersInFlightRef.current || !followersHasMore || !followersCursor) return
    loadMoreFollowersInFlightRef.current = true
    const myGen = followersReqGenRef.current
    setIsLoadingMoreFollowers(true)
    try {
      const result = await apolloClient.query<FollowersData>({
        query: GET_FOLLOWERS,
        variables: {
          uid,
          first: PAGE_SIZE,
          after: followersCursor,
          searchQuery: debouncedSearch || undefined,
        },
        fetchPolicy: 'no-cache',
        context: { jwt },
      })
      if (!isMountedRef.current || myGen !== followersReqGenRef.current) return
      const nextPage = result.data?.userProfileFollowers
      const newEdges = (nextPage?.edges ?? []) as FollowEdge[]
      if (newEdges.length > 0) {
        setExtraFollowers((prev) => [...prev, ...newEdges])
      }
      setFollowersCursor(nextPage?.pageInfo?.endCursor ?? null)
      setFollowersHasMore(!!nextPage?.pageInfo?.hasNextPage)
    } finally {
      loadMoreFollowersInFlightRef.current = false
      if (isMountedRef.current) setIsLoadingMoreFollowers(false)
    }
  }, [
    apolloClient,
    uid,
    jwt,
    followersCursor,
    followersHasMore,
    debouncedSearch,
  ])

  const loadMoreFollowing = useCallback(async (): Promise<void> => {
    if (loadMoreFollowingInFlightRef.current || !followingHasMore || !followingCursor) return
    loadMoreFollowingInFlightRef.current = true
    const myGen = followingReqGenRef.current
    setIsLoadingMoreFollowing(true)
    try {
      const result = await apolloClient.query<FollowingData>({
        query: GET_FOLLOWING,
        variables: {
          uid,
          first: PAGE_SIZE,
          after: followingCursor,
          searchQuery: debouncedSearch || undefined,
        },
        fetchPolicy: 'no-cache',
        context: { jwt },
      })
      if (!isMountedRef.current || myGen !== followingReqGenRef.current) return
      const nextPage = result.data?.userProfileFollowing
      const newEdges = (nextPage?.edges ?? []) as FollowEdge[]
      if (newEdges.length > 0) {
        setExtraFollowing((prev) => [...prev, ...newEdges])
      }
      setFollowingCursor(nextPage?.pageInfo?.endCursor ?? null)
      setFollowingHasMore(!!nextPage?.pageInfo?.hasNextPage)
    } finally {
      loadMoreFollowingInFlightRef.current = false
      if (isMountedRef.current) setIsLoadingMoreFollowing(false)
    }
  }, [
    apolloClient,
    uid,
    jwt,
    followingCursor,
    followingHasMore,
    debouncedSearch,
  ])

  // All mutation-driven cache writes for a follow/unfollow, co-located so
  // they run inside Apollo's mutation cache transaction. Apollo invokes the
  // `update` callback synchronously after the response is written, so
  // broadcastWatches fires once, deterministically — the page-1 useQuery
  // observers (network-only) can't miss the viewerContext flip. Doing these
  // writes AFTER `await mutation` (a separate transaction) is what made the
  // page-1 list update only "sometimes".
  const updateFollowCache = useCallback((
    cache: ApolloCache<unknown>,
    profile: FollowProfile | undefined,
    wasFollowing: boolean,
  ) => {
    if (!profile?.uuid) return
    const isNowFollowing = !wasFollowing

    // 1. Target user's pill state. The mutation auto-normalizes this too,
    //    but the explicit write guarantees the broadcast lands on every
    //    observer of UserProfile:<targetUuid> — including page-1 rows.
    cache.modify({
      id: cache.identify({ __typename: 'UserProfile', uuid: profile.uuid }),
      fields: {
        viewerContext (existing: unknown) {
          if (!existing || typeof existing !== 'object') return existing
          return { ...(existing as Record<string, unknown>), isFollowing: isNowFollowing }
        },
      },
    })

    if (!isViewerProfile) return
    const followingDelta = wasFollowing ? -1 : 1

    // 2. Edge removal on unfollow — no-op if the target lives in
    //    extraFollowing (page 2+); that case is handled in React state by
    //    handleFollowToggle. `countField` is deliberately omitted; totalCount
    //    is decremented below so the badge updates regardless of row source.
    if (wasFollowing) {
      createRemoveItemUpdater({
        entityId: 'ROOT_QUERY',
        field: 'userProfileFollowing',
        edgesPath: 'edges',
        match: (ref, readField) => readField('uid', ref) === profile.uid,
      })(cache)
    }

    // 3. Total-count delta on the viewer's own Following connection. Mutation
    //    response doesn't include the connection's totalCount, so we maintain
    //    it locally. storeFieldName looks like
    //    `userProfileFollowing({"first":20,"uid":"X"})` (and grows a
    //    `"searchQuery":"…"` member when a filter is active). Apollo Client
    //    3.x's ModifierDetails does NOT expose a parsed `args` field —
    //    substring-match against the serialized storeFieldName by uid.
    cache.modify({
      id: 'ROOT_QUERY',
      fields: {
        userProfileFollowing (
          existing: { totalCount?: number } | undefined,
          { storeFieldName }: { storeFieldName: string },
        ) {
          if (!storeFieldName.includes(`"uid":"${uid}"`)) return existing
          const prev = existing?.totalCount ?? 0
          return { ...existing, totalCount: Math.max(0, prev + followingDelta) }
        },
      },
    })

    // 4. Sync the viewer's own UserProfile entity so the gradient header
    //    (and any consumer reading UserProfile:<viewerUuid>.followingCount)
    //    matches without a refetch. `identify` returns undefined if the
    //    entity isn't cached → cache.modify with id:undefined is a no-op.
    if (viewerUuid) {
      cache.modify({
        id: cache.identify({ __typename: 'UserProfile', uuid: viewerUuid }),
        fields: {
          followingCount (existing: number = 0) {
            return Math.max(0, existing + followingDelta)
          },
        },
      })
    }
  }, [isViewerProfile, viewerUuid, uid])

  const handleFollowToggle = useCallback(async (targetUid: string, currentlyFollowing: boolean) => {
    try {
      // Cache writes happen inside the mutation transaction via `update` (see
      // updateFollowCache) so the page-1 broadcast is deterministic.
      if (currentlyFollowing) {
        await unfollowUser({
          variables: { uid: targetUid },
          update: (cache, { data }) => {
            updateFollowCache(cache, data?.userProfileUnfollow?.profile, true)
          },
        })
      } else {
        await followUser({
          variables: { uid: targetUid },
          update: (cache, { data }) => {
            updateFollowCache(cache, data?.userProfileFollow?.profile, false)
          },
        })
      }

      // PostHog: resolve the target's display name from currently-loaded
      // lists so the payload mirrors the public identifier the card showed
      // (no UUIDs in payload — privacy convention).
      const page1Followers = followersResult.data?.userProfileFollowers?.edges ?? []
      const page1Following = followingResult.data?.userProfileFollowing?.edges ?? []
      const matchedNode = page1Followers.find((e) => e.node.uid === targetUid)?.node
        ?? page1Following.find((e) => e.node.uid === targetUid)?.node
        ?? extraFollowers.find((e) => e.node.uid === targetUid)?.node
        ?? extraFollowing.find((e) => e.node.uid === targetUid)?.node
      const targetDisplayName = matchedNode?.displayName || undefined
      if (currentlyFollowing) {
        trackMemberConnectionModalUnfollowed(targetDisplayName)
      } else {
        trackMemberConnectionModalFollowed(targetDisplayName)
      }

      // New follow on the viewer's own Following tab: if the target wasn't
      // already in the loaded list, refetch page 1 so they appear without
      // closing the modal.
      if (isViewerProfile && !currentlyFollowing) {
        const inFollowingList = [...page1Following, ...extraFollowing]
          .some((e) => e.node.uid === targetUid)
        if (!inFollowingList) {
          setExtraFollowing([])
          setFollowingCursor(null)
          setFollowingHasMore(false)
          await followingResult.refetch()
        }
      }

      // Pages 2+ rows live in React state, which is NOT reactive to Apollo's
      // entity normalization — patch the matching edge's pill state here.
      const newIsFollowing = !currentlyFollowing
      const flipExtraEdgeFollowing = (edges: FollowEdge[]): FollowEdge[] => edges.map((e) => (
        e.node.uid === targetUid
          ? {
            ...e,
            node: {
              ...e.node,
              viewerContext: {
                ...e.node.viewerContext,
                isFollowing: newIsFollowing,
              },
            },
          }
          : e
      ))
      if (isMountedRef.current) {
        setExtraFollowers((prev) => flipExtraEdgeFollowing(prev))
        setExtraFollowing((prev) => flipExtraEdgeFollowing(prev))
        // Page-2+ edge removal on unfollow from the viewer's own Following
        // tab (the cache updater above only touches the page-1 slot).
        if (isViewerProfile && currentlyFollowing) {
          setExtraFollowing((prev) => prev.filter((e) => e.node.uid !== targetUid))
        }
      }
    } catch {
      // Stay in current state on failure
    }
  }, [
    followUser,
    unfollowUser,
    updateFollowCache,
    followersResult.data,
    followingResult.data,
    extraFollowers,
    extraFollowing,
    trackMemberConnectionModalFollowed,
    trackMemberConnectionModalUnfollowed,
    isViewerProfile,
    followingResult,
  ])

  // Combined edges (page 1 + extras) per tab — the rendered list reads
  // from these memos.
  const combinedFollowersEdges = useMemo<FollowEdge[]>(() => {
    const page1 = followersResult.data?.userProfileFollowers?.edges ?? []
    return [...page1, ...extraFollowers]
  }, [followersResult.data, extraFollowers])

  const combinedFollowingEdges = useMemo<FollowEdge[]>(() => {
    const page1 = followingResult.data?.userProfileFollowing?.edges ?? []
    return [...page1, ...extraFollowing]
  }, [followingResult.data, extraFollowing])

  // Totals come from page-1 responses (Apollo cache). `cache.modify` keeps
  // these consistent across mutations on the viewer's own modal.
  const followersCount = followersResult.data?.userProfileFollowers?.totalCount ?? null
  const followingCount = followingResult.data?.userProfileFollowing?.totalCount ?? null

  // Active-tab selectors for the rendered list + LoadMore sentinel.
  // hasMore is consumed via per-tab guards inside loadMoreFollowers /
  // loadMoreFollowing, so the LoadMore component derives its own visibility
  // from loadedCount vs totalCount and doesn't need an `activeHasMore` prop.
  const activeEdges = activeTab === 'followers' ? combinedFollowersEdges : combinedFollowingEdges
  const loadedCount = activeEdges.length
  const totalCount = (activeTab === 'followers' ? followersCount : followingCount) ?? 0
  const activeIsLoadingMore = activeTab === 'followers' ? isLoadingMoreFollowers : isLoadingMoreFollowing

  const tabItems = useMemo<Array<{ key: TabKey; label: string; count: number | null }>>(() => [
    { key: 'followers', label: followersLabel, count: followersCount },
    { key: 'following', label: followingLabel, count: followingCount },
  ], [followersCount, followingCount, followersLabel, followingLabel])

  const getAvatarUrl = (user: UserProfileSummary) => {
    const primaryAvatar = user.avatar
    const batchAvatar = user.uid ? batchAvatarByUid[user.uid] : undefined
    return primaryAvatar?.card
      || primaryAvatar?.small
      || primaryAvatar?.url
      || batchAvatar?.card
      || batchAvatar?.small
      || batchAvatar?.url
  }

  // Server-side filtering via the `searchQuery` GraphQL arg replaces the
  // previous in-memory `.filter(...)` pass — each page already reflects the
  // active filter, so this is now a straight map from edges to nodes.
  const getConnections = (): UserProfileSummary[] => activeEdges.map((e) => e.node)

  const getIsLoading = (): boolean => {
    // Only treat as "loading" on the very first fetch for this tab — when
    // Apollo has no cached page-1 data yet. Once data has arrived, keep the
    // last rows on screen across subsequent updates (mutations, tab switches)
    // so we don't flash the placeholder.
    if (activeTab === 'followers') {
      return followersResult.loading && !followersResult.data?.userProfileFollowers
    }
    if (activeTab === 'following') {
      return followingResult.loading && !followingResult.data?.userProfileFollowing
    }
    return false
  }

  const connections = getConnections()
  const isLoading = getIsLoading()

  if (!isBrowser()) return null

  return createPortal(
    <div className="connections-modal__overlay" onClick={() => onClose()} role="presentation">
      <div className="connections-modal" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="connections-modal__header">
          <H5 className="connections-modal__title">{
            (() => {
              const trimmedName = (displayName ?? '').trim()
              // Capitalize only the FIRST character — mixed-case usernames
              // ("looshfarmingdeva222@gaia") read as a sentence-cased proper
              // noun in the title, while internal characters stay verbatim
              // (don't lower-case the rest, which would break "GaiaUser" or
              // similar already-cased names).
              const titleName = trimmedName
                ? trimmedName.charAt(0).toUpperCase() + trimmedName.slice(1)
                : ''
              if (titleName) {
                return titleWithNameTemplate.replace('{name}', titleName)
              }
              return titleText
            })()
          }</H5>
          <Button
            className="connections-modal__close"
            onClick={() => onClose()}
            type={BUTTON_TYPES.GHOST}
            aria-label={closeLabel}
          >
            &times;
          </Button>
        </div>
        <div className="connections-modal__tabs">
          <CommunityTabBar<TabKey>
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={(key) => setActiveTab(key as TabKey)}
          />
        </div>
        <div className="connections-modal__search">
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="connections-modal__search-input"
          />
        </div>
        <div className="connections-modal__list">
          {isLoading && Array.from({ length: LOADING_PLACEHOLDER_COUNT }).map((_, idx) => (
            <div className="connections-modal__loading-row" key={`loading-row-${idx}`} aria-hidden="true">
              <div className="connections-modal__loading-avatar" />
              <div className="connections-modal__loading-content">
                <div className="connections-modal__loading-line connections-modal__loading-line--name" />
                <div className="connections-modal__loading-line connections-modal__loading-line--meta" />
              </div>
              <div className="connections-modal__loading-pill" />
            </div>
          ))}
          {!isLoading && connections.length === 0 && search.trim() ? (
            <div className="connections-modal__empty">
              {emptySearchTemplate.replace('{query}', search)}
            </div>
          ) : null}
          {!isLoading && connections.map((user) => {
            const hasConnection = !!(
              user.viewerContext?.isFollowing || user.viewerContext?.isFollowedBy
            )
            const isPrivate = user.profileVisibility === 'private'
              && !user.viewerContext?.isOwner
              && !hasConnection
            const avatarUrl = getAvatarUrl(user)
            return (
              <ConnectionCard
                key={user.uid}
                uid={user.uid || ''}
                uuid={user.uuid || ''}
                displayName={user.displayName || defaultDisplayName}
                avatarUrl={avatarUrl}
                followsViewer={user.viewerContext?.isFollowedBy}
                isFollowing={user.viewerContext?.isFollowing}
                isAnonymous={isPrivate}
                isViewer={!!user.viewerContext?.isOwner}
                onFollowToggle={handleFollowToggle}
                anonymousLabel={cardAnonymous}
                followsYouLabel={cardFollowsYou}
              />
            )
          })}
          {!isLoading ? (
            // @ts-expect-error LoadMore's default export carries a bogus `@type {LoadMore}`
            // JSDoc (lib/components/LoadMore/LoadMore.jsx) that types the connected export as a
            // class instance, not a component. Suppressed here to avoid editing shared infra.
            <LoadMore
              onLoadMore={activeTab === 'followers' ? loadMoreFollowers : loadMoreFollowing}
              currentCount={loadedCount}
              totalCount={totalCount}
              processing={activeIsLoadingMore}
              infiniteScroll
              buttonClassName={['connections-modal__load-more-button']}
            />
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}

function ConnectionsModal ({
  uid,
  initialTab = 'followers',
  onClose,
  jwt,
  displayName,
  isViewerProfile = false,
  viewerUuid,
  staticText,
}: ConnectionsModalProps) {
  return (
    <StaticTextProvider staticText={staticText}>
      <ConnectionsModalContent
        uid={uid}
        initialTab={initialTab}
        onClose={onClose}
        jwt={jwt}
        displayName={displayName}
        isViewerProfile={isViewerProfile}
        viewerUuid={viewerUuid}
      />
    </StaticTextProvider>
  )
}

export default connectStaticText({ storeKey: 'connectionsModal' })(ConnectionsModal)
